/**
 * Azure Speech Service Integration
 * Handles real-time speech recognition and audio transcription
 */

import * as sdk from 'microsoft-cognitiveservices-speech-sdk';

class SpeechService {
  constructor() {
    this.speechConfig = null;
    this.recognizer = null;
    this.isListening = false;
    this.audioConfig = null;
    this.currentCallbacks = null;
    this.shouldRestart = false;
    this.restartAttempts = 0;
    this.maxRestartAttempts = 5;

    this.initializeSpeechConfig();
  }

  /**
   * Initialize Azure Speech configuration
   */
  initializeSpeechConfig() {
    try {
      const speechKey = process.env.REACT_APP_SPEECH_KEY;
      const speechRegion = process.env.REACT_APP_SPEECH_REGION;

      if (!speechKey || !speechRegion) {
        console.error('Azure Speech credentials missing in environment variables');
        return false;
      }

      this.speechConfig = sdk.SpeechConfig.fromSubscription(speechKey, speechRegion);
      this.speechConfig.speechRecognitionLanguage = 'en-US';

      // Use minimal configuration to avoid WebSocket issues
      // this.speechConfig.enableDictation(); // Temporarily disable to test

      // Use default timeout settings initially
      // this.speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, "15000");
      // this.speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, "30000");
      // this.speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "10000");

      // Use conversation mode which is more stable for continuous recognition
      this.speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_RecoMode, "CONVERSATION");
      
      console.log('Azure Speech Service initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Azure Speech Service:', error);
      return false;
    }
  }

  /**
   * Start continuous speech recognition
   * @param {Object} callbacks - Event callbacks
   * @param {Function} callbacks.onTranscribing - Called during live transcription
   * @param {Function} callbacks.onRecognized - Called when speech segment is finalized
   * @param {Function} callbacks.onError - Called on recognition errors
   * @param {Function} callbacks.onSessionStopped - Called when session ends
   */
  async startContinuousRecognition(callbacks = {}) {
    try {
      if (!this.speechConfig) {
        throw new Error('Speech service not initialized');
      }

      // Store callbacks for potential restarts
      this.currentCallbacks = callbacks;
      this.shouldRestart = true;
      this.restartAttempts = 0;

      return await this._startRecognitionSession(callbacks);
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      if (callbacks.onError) {
        callbacks.onError(error.message);
      }
      return { success: false, error: error.message };
    }
  }

  async _startRecognitionSession(callbacks, isRestart = false) {
    try {
      let stream = null;

      // Only request microphone permission on initial start, not on restarts
      if (!isRestart) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true // Use simple audio config to avoid issues
        });
      }

      // Create audio config from microphone
      this.audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

      // Create speech recognizer
      this.recognizer = new sdk.SpeechRecognizer(this.speechConfig, this.audioConfig);

      // Event handlers
      this.recognizer.recognizing = (sender, e) => {
        // Do not log transcript content (PHI).
        if (callbacks.onTranscribing && e.result.text) {
          callbacks.onTranscribing(e.result.text);
        }
      };

      this.recognizer.recognized = (sender, e) => {
        // Do not log transcript content (PHI).
        if (callbacks.onRecognized && e.result.text) {
          callbacks.onRecognized(e.result.text);
        }
      };

      this.recognizer.sessionStarted = (sender, e) => {
        console.log('Speech recognition session started');
      };

      this.recognizer.canceled = (sender, e) => {
        console.log('Speech recognition canceled:', e.reason, e.errorDetails);

        // Handle specific WebSocket errors
        if (e.errorDetails && e.errorDetails.includes('websocket error code: 1007')) {
          console.log('WebSocket protocol error detected, attempting to restart...');
          if (callbacks.onError) {
            callbacks.onError('Connection lost. Please try recording again.');
          }
        } else {
          if (callbacks.onError) {
            callbacks.onError(`Recognition canceled: ${e.errorDetails || e.reason}`);
          }
        }
        this.stopRecognition();
      };

      this.recognizer.sessionStopped = (sender, e) => {
        console.log('Speech recognition session stopped');
        console.log('shouldRestart:', this.shouldRestart);
        console.log('restartAttempts:', this.restartAttempts);
        console.log('maxRestartAttempts:', this.maxRestartAttempts);

        // Don't set isListening to false if we're restarting
        if (!this.shouldRestart || this.restartAttempts >= this.maxRestartAttempts) {
          this.isListening = false;
          console.log('Setting isListening to false - restart conditions not met');
        }

        // Automatically restart if we should continue listening and haven't exceeded max attempts
        if (this.shouldRestart && this.restartAttempts < this.maxRestartAttempts) {
          this.restartAttempts++;
          console.log(`🔄 Restarting speech recognition session (attempt ${this.restartAttempts}/${this.maxRestartAttempts})`);

          // Restart immediately without delay for seamless experience
          setTimeout(async () => {
            try {
              console.log('🔧 Cleaning up current recognizer before restart');
              // Clean up current recognizer before restart
              if (this.recognizer) {
                this.recognizer.close();
                this.recognizer = null;
              }

              console.log('🚀 Starting new recognition session');
              await this._startRecognitionSession(this.currentCallbacks, true);
              console.log('✅ Recognition session restarted successfully');
            } catch (error) {
              console.error('❌ Failed to restart speech recognition:', error);
              this.isListening = false;
              if (this.currentCallbacks && this.currentCallbacks.onError) {
                this.currentCallbacks.onError('Speech recognition stopped unexpectedly');
              }
            }
          }, 100); // Minimal delay for cleanup
        } else {
          // Final stop - call the session stopped callback
          console.log('🛑 Final stop - not restarting');
          this.isListening = false;
          if (callbacks.onSessionStopped) {
            callbacks.onSessionStopped();
          }
        }
      };

      // Start continuous recognition
      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('Continuous speech recognition started');
          this.isListening = true;
        },
        (error) => {
          console.error('Failed to start speech recognition:', error);
          if (callbacks.onError) {
            callbacks.onError(`Failed to start recognition: ${error}`);
          }
        }
      );

      return { success: true, stream };

    } catch (error) {
      console.error('Error starting speech recognition:', error);
      if (callbacks.onError) {
        callbacks.onError(error.message);
      }
      return { success: false, error: error.message };
    }
  }

  /**
   * Stop speech recognition
   */
  stopRecognition() {
    try {
      // Stop automatic restarts
      console.log('🛑 stopRecognition called - disabling restarts');
      this.shouldRestart = false;
      this.currentCallbacks = null;

      if (this.recognizer && this.isListening) {
        this.recognizer.stopContinuousRecognitionAsync(
          () => {
            console.log('Speech recognition stopped');
            this.cleanup();
          },
          (error) => {
            console.error('Error stopping speech recognition:', error);
            this.cleanup();
          }
        );
      }
    } catch (error) {
      console.error('Error stopping recognition:', error);
      this.cleanup();
    }
  }

  /**
   * Process uploaded audio file
   * @param {File} audioFile - Audio file to transcribe
   * @param {Function} onProgress - Progress callback
   */
  async transcribeAudioFile(audioFile, onProgress = null) {
    try {
      if (!this.speechConfig) {
        throw new Error('Speech service not initialized');
      }

      // Convert file to buffer
      const arrayBuffer = await audioFile.arrayBuffer();
      const audioBuffer = new Uint8Array(arrayBuffer);

      // Create audio config from buffer
      const pushStream = sdk.AudioInputStream.createPushStream();
      pushStream.write(audioBuffer);
      pushStream.close();

      const audioConfig = sdk.AudioConfig.fromStreamInput(pushStream);
      const recognizer = new sdk.SpeechRecognizer(this.speechConfig, audioConfig);

      return new Promise((resolve, reject) => {
        let fullTranscript = '';

        recognizer.recognized = (sender, e) => {
          if (e.result.text) {
            fullTranscript += e.result.text + ' ';
            if (onProgress) {
              onProgress(fullTranscript.trim());
            }
          }
        };

        recognizer.sessionStopped = () => {
          recognizer.close();
          resolve(fullTranscript.trim());
        };

        recognizer.canceled = (sender, e) => {
          console.error('File transcription canceled:', e.reason);
          recognizer.close();
          reject(new Error(`Transcription failed: ${e.errorDetails || e.reason}`));
        };

        recognizer.startContinuousRecognitionAsync();
      });

    } catch (error) {
      console.error('Error transcribing audio file:', error);
      throw error;
    }
  }

  /**
   * Get available audio input devices
   */
  async getAudioDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          groupId: device.groupId
        }));

      return { success: true, devices: audioInputs };
    } catch (error) {
      console.error('Error getting audio devices:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if speech recognition is currently active
   */
  isRecognizing() {
    return this.isListening;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    try {
      this.shouldRestart = false;
      this.currentCallbacks = null;
      this.restartAttempts = 0;

      if (this.recognizer) {
        this.recognizer.close();
        this.recognizer = null;
      }

      if (this.audioConfig) {
        this.audioConfig = null;
      }

      this.isListening = false;
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  /**
   * Test speech service connection
   */
  async testConnection() {
    try {
      if (!this.speechConfig) {
        return { success: false, error: 'Speech service not initialized' };
      }

      // Create a simple test recognizer
      const testAudioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
      const testRecognizer = new sdk.SpeechRecognizer(this.speechConfig, testAudioConfig);

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          testRecognizer.close();
          resolve({ success: false, error: 'Connection test timeout' });
        }, 5000);

        testRecognizer.sessionStarted = () => {
          clearTimeout(timeout);
          testRecognizer.close();
          resolve({ success: true, message: 'Azure Speech Service connection successful' });
        };

        testRecognizer.canceled = (sender, e) => {
          clearTimeout(timeout);
          testRecognizer.close();
          resolve({ 
            success: false, 
            error: `Connection test failed: ${e.errorDetails || e.reason}` 
          });
        };

        // Start a brief recognition session
        testRecognizer.recognizeOnceAsync();
      });

    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// Create and export singleton instance
export const speechService = new SpeechService();
export default speechService;